import { useEffect, useMemo, useRef } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useAllUpsells from 'hooks/Upsells/useAllUpsells'
import { addDays, subDays } from 'lib/datetime/dateUtils'
import { EmptyArray } from 'lib/array/arrayUtils'
import noop from 'lib/function/noop'
import { getUpcomingOrders } from 'selectors/orderSelectors'
import fetchEsimPromoCodes from 'actions/loyalty/fetchEsimPromoCodes'
import useLoyaltyLoungePassData from 'loyalty/hooks/useLoyaltyLoungePassData'
import useLoyaltyUserBenefitView from 'loyalty/hooks/useLoyaltyUserBenefitView'
import useLoyaltyOrderEsimBenefitView from 'loyalty/hooks/useLoyaltyOrderViewEsimData'
import getIsLoyaltyEsimEnabled from 'loyalty/selectors/getIsLoyaltyEsimEnabled'
import getLoyaltyAccountRegion from 'loyalty/selectors/getLoyaltyAccountRegion'
import getUpcomingOrdersEligibleForLoungePassRedemption, {
  getPreQualifyingOrdersWithLoungeFilters,
} from 'checkout/selectors/loyalty/getUpcomingOrdersEligibleForLoungePassRedemption'
import { getOrderDateRange } from 'checkout/Components/Confirmation/BookingDetailsV2/utils'
import { getEsimGbForTier, isPastEsimTripEnd } from 'loyalty/lib/esims/loyaltyEsimUtils'
import checkExistingUpsellInDateWindow from 'lib/order/checkExistingUpsellInDateWindow'
import getUpcomingEscapeRowEligibility, {
  isUpcomingEscapeCommercialLoading,
  type UpcomingEscapeRowEligibility,
} from './getUpcomingEscapeRowEligibility'
import useLoadTimeout from './useLoadTimeout'
import useLoyaltyOrderViewTransferData from 'loyalty/hooks/useLoyaltyOrderViewTransferData'
import useLoyaltyOrderItineraries from 'loyalty/hooks/useLoyaltyOrderItineraries'
import getUpcomingEscapeTransferEligibility, { getUpcomingEscapeNeedsTransferItinerary } from './getUpcomingEscapeTransferEligibility'

/** Hung commercial fetches unstick row gates so a stalled request cannot hide a row forever. */
export const UPCOMING_ESCAPE_LOAD_TIMEOUT_MS = 8000

export interface UpcomingEscapeUpsellsResult {
  eligibility: UpcomingEscapeRowEligibility
  flightUpsell: ReturnType<typeof useAllUpsells>['flight']['flightUpsell']
  insuranceUpsell: ReturnType<typeof useAllUpsells>['insurance']['insuranceUpsell']
  insuranceBookingProtectionUpsell: ReturnType<typeof useAllUpsells>['insuranceBookingProtection']['insuranceBookingProtectionUpsell']
  carHireUpsell: ReturnType<typeof useAllUpsells>['carHire']['carHireUpsell']
  tourUpsell: ReturnType<typeof useAllUpsells>['tour']['tourUpsell']
  airportTransferUpsell: ReturnType<typeof useAllUpsells>['airportTransfer']['airportTransferUpsell']
  handleFlightUpsellDismiss: () => void
  handleInsuranceUpsellDismiss: () => void
  handleInsuranceBookingProtectionUpsellDismiss: () => void
  handleInsuranceBookingProtectionSelect: () => void
  handleCarHireUpsellDismiss: () => void
  handleTourUpsellDismiss: () => void
  handleAirportTransferDismiss: () => void
  loungeFilters: ReturnType<typeof getPreQualifyingOrdersWithLoungeFilters>['filtersByOrderId'][string]
}

function useUpcomingEscapeUpsells(order: App.Order): UpcomingEscapeUpsellsResult {
  const dispatch = useAppDispatch()
  const upcomingOrders = useAppSelector(getUpcomingOrders)
  const loyaltyConfig = useAppSelector(state => state.loyalty.config)

  const {
    insurance: {
      insuranceUpsellEnabled,
      insuranceUpsell,
      handleInsuranceUpsellDismiss,
      insuranceDataLoading,
    },
    insuranceBookingProtection: {
      insuranceBookingProtectionUpsellEnabled,
      insuranceBookingProtectionUpsell,
      handleInsuranceBookingProtectionUpsellDismiss,
      handleInsuranceBookingProtectionSelect,
      insuranceBookingProtectionDataLoading,
    },
    flight: {
      flightUpsellEnabled,
      flightUpsell,
      handleFlightUpsellDismiss,
      flightDataLoading,
    },
    carHire: {
      carHireUpsellEnabled,
      carHireUpsell,
      handleCarHireUpsellDismiss,
      carHireDataLoading,
    },
    tour: {
      tourUpsellEnabled,
      tourUpsell,
      handleTourUpsellDismiss,
      tourDataLoading,
    },
    airportTransfer: {
      airportTransferUpsellEnabled,
      airportTransferUpsell,
      handleAirportTransferDismiss,
      airportTransferDataLoading,
    },
  } = useAllUpsells(order)

  const upsellOrderDates = useMemo(() => getOrderDateRange(order), [order])
  const { hasExistingCarHire, hasExistingFlight, hasExistingTour, hasExistingTransfer } = useMemo(() => {
    const twoWeeksBeforeEarliestDate = subDays(new Date(upsellOrderDates.earliestDate ?? new Date()), 14)
    const twoWeeksAfterLatestDate = addDays(new Date(upsellOrderDates.latestDate ?? new Date()), 14)

    return {
      hasExistingCarHire: checkExistingUpsellInDateWindow(
        upcomingOrders,
        twoWeeksBeforeEarliestDate,
        twoWeeksAfterLatestDate,
        orderItem => orderItem.carHireItems?.map(item => item.reservation?.pickUp?.date) ?? [],
      ),
      hasExistingFlight: checkExistingUpsellInDateWindow(
        upcomingOrders,
        twoWeeksBeforeEarliestDate,
        twoWeeksAfterLatestDate,
        orderItem => orderItem.flightItems?.map(item => item.departureDate) ?? [],
      ),
      hasExistingTour: checkExistingUpsellInDateWindow(
        upcomingOrders,
        twoWeeksBeforeEarliestDate,
        twoWeeksAfterLatestDate,
        orderItem => orderItem.tourItems?.map(item => item.tour?.startDate) ?? [],
      ),
      hasExistingTransfer: checkExistingUpsellInDateWindow(
        upcomingOrders,
        twoWeeksBeforeEarliestDate,
        twoWeeksAfterLatestDate,
        orderItem => orderItem.transferItems?.map(item => item.date) ?? [],
      ),
    }
  }, [upcomingOrders, upsellOrderDates])

  useLoyaltyOrderViewTransferData({ order })
  const transferBenefitEligibleForOrder = useAppSelector(state => getUpcomingEscapeTransferEligibility(state, order))

  const needsTransferItinerary = useAppSelector(state => getUpcomingEscapeNeedsTransferItinerary(state, order))
  const transferOrders = useMemo(() => [order], [order])
  useLoyaltyOrderItineraries(transferOrders, needsTransferItinerary)

  const loungeFilters = useAppSelector(getPreQualifyingOrdersWithLoungeFilters).filtersByOrderId[order.id] ?? EmptyArray
  useLoyaltyLoungePassData({ filters: loungeFilters })
  const loungeEligibleOrders = useAppSelector(getUpcomingOrdersEligibleForLoungePassRedemption)
  const loungeBenefit = useLoyaltyUserBenefitView('lounge_pass')

  const esimEnabled = useAppSelector(getIsLoyaltyEsimEnabled)
  const accountRegion = useAppSelector(getLoyaltyAccountRegion)
  const esimBenefit = useLoyaltyUserBenefitView('esim_package')
  const esimBenefitOrderIds = useAppSelector(state => state.loyalty.esimPromoCodes.benefitOrderIds)
  const esimBenefitListLoaded = useAppSelector(state => state.loyalty.esimPromoCodes.benefitListLoaded)
  const hasEsimBenefit = !!esimBenefit && (esimBenefit.benefit.unlimited || !!esimBenefit.benefit.remaining)
  const orderEsimBenefit = order.loyaltyDetails?.esimBenefit
  const hasOrderEsimAllowance = !!orderEsimBenefit
    && getEsimGbForTier(loyaltyConfig, orderEsimBenefit.tier) !== undefined
  const isPastEsimTrip = isPastEsimTripEnd(orderEsimBenefit?.endDate)

  const isCommercialUpsellLoading = isUpcomingEscapeCommercialLoading({
    flightDataLoading,
    carHireDataLoading,
    insuranceDataLoading,
    insuranceBookingProtectionDataLoading,
    tourDataLoading,
    airportTransferDataLoading,
  })

  const esimBenefitView = useLoyaltyOrderEsimBenefitView(order.id)
  const hasLoungeBenefit = !!loungeBenefit
  const hasFlightUpsell = !!flightUpsell
  const hasFlightTitle = !!flightUpsell?.flightTitle
  const hasInsuranceUpsell = !!insuranceUpsell
  const hasInsuranceBookingProtectionUpsell = !!insuranceBookingProtectionUpsell
  const hasTourUpsell = !!tourUpsell
  const hasAirportTransferUpsell = !!airportTransferUpsell
  const hasCarHireUpsell = !!carHireUpsell
  const hasEsimBenefitView = !!esimBenefitView && !esimBenefitView.tripEnded

  const commercialTimedOut = useLoadTimeout(isCommercialUpsellLoading, UPCOMING_ESCAPE_LOAD_TIMEOUT_MS)

  const commercialSettledOnceRef = useRef(false)
  const settledOrderIdRef = useRef(order.id)
  if (settledOrderIdRef.current !== order.id) {
    settledOrderIdRef.current = order.id
    commercialSettledOnceRef.current = false
  }
  if (!isCommercialUpsellLoading || commercialTimedOut) {
    commercialSettledOnceRef.current = true
  }
  // Sticky after first commercial settle so refetch loading cannot hide rows.
  const ignoreCommercialLoadingFlicker = commercialSettledOnceRef.current

  const eligibility = useMemo(() => getUpcomingEscapeRowEligibility({
    // After timeout / first commercial settle, ignore loading flashes so hung or refetching
    // fetches cannot hide rows that can already render from available upsell data.
    flightDataLoading: flightDataLoading && !commercialTimedOut && !ignoreCommercialLoadingFlicker,
    flightUpsellEnabled,
    hasExistingFlight,
    carHireDataLoading: carHireDataLoading && !commercialTimedOut && !ignoreCommercialLoadingFlicker,
    carHireUpsellEnabled,
    hasExistingCarHire,
    insuranceDataLoading: insuranceDataLoading && !commercialTimedOut && !ignoreCommercialLoadingFlicker,
    insuranceUpsellEnabled,
    // Timeout unmasks a stuck CFMR fetch only for the loading flag. The row
    // still requires hasInsuranceBookingProtectionUpsell, so we never paint an
    // Add CTA before a catalog product exists.
    insuranceBookingProtectionDataLoading: insuranceBookingProtectionDataLoading && !commercialTimedOut && !ignoreCommercialLoadingFlicker,
    insuranceBookingProtectionUpsellEnabled,
    tourDataLoading: tourDataLoading && !commercialTimedOut && !ignoreCommercialLoadingFlicker,
    tourUpsellEnabled,
    hasExistingTour,
    airportTransferDataLoading: airportTransferDataLoading && !commercialTimedOut && !ignoreCommercialLoadingFlicker,
    airportTransferUpsellEnabled,
    hasExistingTransfer,
    hasFlightUpsell,
    hasFlightTitle,
    hasInsuranceUpsell,
    hasInsuranceBookingProtectionUpsell,
    hasTourUpsell,
    hasAirportTransferUpsell,
    hasCarHireUpsell,
    transferBenefitEligibleForOrder,
    hasLoungeBenefit,
    loungeEligibleForOrder: loungeEligibleOrders.some(eligible => eligible.id === order.id),
    esimEnabled,
    hasEsimBenefit,
    esimBenefitListLoaded,
    esimEligibleForOrder: esimBenefitOrderIds.includes(order.id),
    hasOrderEsimAllowance,
    isPastEsimTrip,
    hasEsimBenefitView,
  }), [
    flightDataLoading,
    commercialTimedOut,
    flightUpsellEnabled,
    hasExistingFlight,
    carHireDataLoading,
    carHireUpsellEnabled,
    hasExistingCarHire,
    insuranceDataLoading,
    insuranceUpsellEnabled,
    insuranceBookingProtectionDataLoading,
    insuranceBookingProtectionUpsellEnabled,
    tourDataLoading,
    tourUpsellEnabled,
    hasExistingTour,
    airportTransferDataLoading,
    airportTransferUpsellEnabled,
    hasExistingTransfer,
    hasFlightUpsell,
    hasFlightTitle,
    hasInsuranceUpsell,
    hasInsuranceBookingProtectionUpsell,
    hasTourUpsell,
    hasAirportTransferUpsell,
    hasCarHireUpsell,
    transferBenefitEligibleForOrder,
    hasLoungeBenefit,
    loungeEligibleOrders,
    order.id,
    esimEnabled,
    hasEsimBenefit,
    esimBenefitListLoaded,
    esimBenefitOrderIds,
    hasOrderEsimAllowance,
    isPastEsimTrip,
    hasEsimBenefitView,
    ignoreCommercialLoadingFlicker,
  ])

  useEffect(() => {
    if (!accountRegion || !hasEsimBenefit || !esimEnabled) return
    dispatch(fetchEsimPromoCodes())
  }, [dispatch, accountRegion, hasEsimBenefit, esimEnabled])

  return useMemo(() => ({
    eligibility,
    flightUpsell,
    insuranceUpsell,
    insuranceBookingProtectionUpsell,
    carHireUpsell,
    tourUpsell,
    airportTransferUpsell,
    handleFlightUpsellDismiss: handleFlightUpsellDismiss ?? noop,
    handleInsuranceUpsellDismiss: handleInsuranceUpsellDismiss ?? noop,
    handleInsuranceBookingProtectionUpsellDismiss: handleInsuranceBookingProtectionUpsellDismiss ?? noop,
    handleInsuranceBookingProtectionSelect: handleInsuranceBookingProtectionSelect ?? noop,
    handleCarHireUpsellDismiss: handleCarHireUpsellDismiss ?? noop,
    handleTourUpsellDismiss: handleTourUpsellDismiss ?? noop,
    handleAirportTransferDismiss: handleAirportTransferDismiss ?? noop,
    loungeFilters,
  }), [
    eligibility,
    flightUpsell,
    insuranceUpsell,
    insuranceBookingProtectionUpsell,
    carHireUpsell,
    tourUpsell,
    airportTransferUpsell,
    handleFlightUpsellDismiss,
    handleInsuranceUpsellDismiss,
    handleInsuranceBookingProtectionUpsellDismiss,
    handleInsuranceBookingProtectionSelect,
    handleCarHireUpsellDismiss,
    handleTourUpsellDismiss,
    handleAirportTransferDismiss,
    loungeFilters,
  ])
}

export default useUpcomingEscapeUpsells
